import {
  resolveImpulseAccuracy,
  sampleAccuracyRolls,
} from "../dynamics/accuracy";
import { simulateSimultaneousImpulses } from "../dynamics/simulation";
import type { SimulationEntityState, Vector2D } from "../types/dynamics";
import type { StoneFlickingCoreConfig } from "../types/game-config";
import type { RoundInput, StoneFlickingCoreState } from "../types/game-state";
import type { RandomProvider } from "../types/random-provider";
import { createMathRandomProvider } from "../utils/random-provider";
import { evaluateOutcome } from "./evaluate-outcome.applier";

export function applyResolveRound(
  state: StoneFlickingCoreState,
  config: StoneFlickingCoreConfig,
  randomProvider?: RandomProvider,
): StoneFlickingCoreState {
  const nonNullInputs: RoundInput[] = [];
  for (const [, input] of state.pendingInputs) {
    if (input !== null) {
      nonNullInputs.push(input);
    }
  }

  const impulses: Array<{
    readonly entityId: string;
    readonly impulse: Vector2D;
  }> = [];
  for (const input of nonNullInputs) {
    let effectiveImpulse: Vector2D = input.impulse;

    if (config.impulseAccuracy) {
      const provider = randomProvider ?? createMathRandomProvider();
      const rolls = sampleAccuracyRolls(provider);
      effectiveImpulse = resolveImpulseAccuracy(
        input.impulse,
        config.impulseAccuracy,
        rolls,
      );
    }

    impulses.push({ entityId: input.entityId, impulse: effectiveImpulse });
  }

  const simulation = simulateSimultaneousImpulses(
    state.entities,
    impulses,
    config,
  );

  const finalStatesById = new Map<string, SimulationEntityState>();
  for (const item of simulation.finalEntityStates) {
    finalStatesById.set(item.id, item);
  }

  const updatedEntities = state.entities.map((entity) => {
    const finalState = finalStatesById.get(entity.id);
    if (!finalState) return entity;

    return {
      ...entity,
      x: finalState.x,
      y: finalState.y,
      eliminated: finalState.eliminated,
    };
  });

  const afterSimulation: StoneFlickingCoreState = {
    ...state,
    phase: "RESOLVING",
    entities: updatedEntities,
    lastRecording: simulation.recording,
  };

  const afterOutcome = evaluateOutcome(afterSimulation, config);
  if (afterOutcome.phase === "ENDED") {
    return afterOutcome;
  }

  return {
    ...afterOutcome,
    phase: "INPUT_PHASE",
    round: state.round + 1,
    pendingInputs: new Map(),
    submittedParticipants: [],
  };
}
